/**
 * Atlas PM · 任务详情（设计稿 12）· 右列：审批时间轴 + 关键字段实时
 *
 * 时间轴 = 操作留痕（历史） + 审批时间线（提交/通过/驳回） + 审批模型的当前/待审节点。
 * 关键字段实时 = 进度 / 指标达标 / 产出物齐套 / 逾期风险 / 冻结状态 / 操作记录。
 */

import Badge from '../../components/Badge';
import { taskStatusLabel } from '@atlaspm/shared';
import type {
  ApprovalInstanceInfo,
  ApprovalTimelineItem,
  CompletenessInfo,
  TaskDetail,
  TaskProgressInfo,
} from '@atlaspm/shared';
import type { AuditLogItem } from './useTaskDetail';

export interface DetailTimelineColumnProps {
  task: TaskDetail;
  progress?: TaskProgressInfo;
  completeness?: CompletenessInfo;
  approval?: ApprovalInstanceInfo | null;
  timeline?: ApprovalTimelineItem[];
  logs?: AuditLogItem[];
}

interface TlNode {
  key: string;
  title: string;
  time?: string;
  actor?: string;
  desc?: string;
  tone: 'ok' | 'run' | 'warn' | 'alert' | 'idle' | 'frozen';
  pending?: boolean;
}

function fmt(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function stateLabel(state: unknown): string {
  return taskStatusLabel(state == null ? null : String(state));
}

function auditNode(log: AuditLogItem): TlNode {
  const action = log.action || '';
  const to = (log.diff?.state as { to?: unknown } | undefined)?.to;
  let title = action;
  let tone: TlNode['tone'] = 'idle';
  if (action === 'TASK_CREATE') {
    title = '创建任务';
    tone = 'run';
  } else if (action === 'TASK_UPDATE' && to !== undefined) {
    title = `状态更新为「${stateLabel(to)}」`;
    tone = String(to) === 'FROZEN' ? 'frozen' : String(to) === 'SUBMITTED' ? 'warn' : 'run';
  } else if (action === 'METRIC_CREATE') {
    title = '新增指标';
    tone = 'run';
  } else if (action === 'ELEMENT_CREATE') {
    title = '新增要素';
    tone = 'run';
  } else if (action === 'ELEMENT_VALUE_SET') {
    title = '填写要素';
    tone = 'run';
  } else if (action.includes('FREEZE')) {
    title = '冻结任务';
    tone = 'frozen';
  } else if (action.includes('APPROVE')) {
    title = '审批通过';
    tone = 'ok';
  } else if (action.includes('REJECT')) {
    title = '审批驳回';
    tone = 'alert';
  } else if (action.includes('DELIVERABLE')) {
    title = '产出物变更';
    tone = 'run';
  }
  return {
    key: `audit-${log.id}`,
    title,
    time: log.created_at,
    actor: log.actor_name ?? undefined,
    tone,
  };
}

function approvalTone(action: string): TlNode['tone'] {
  const a = action.toUpperCase();
  if (a.includes('APPROVE') || a.includes('PASS')) return 'ok';
  if (a.includes('REJECT') || a.includes('RETURN')) return 'alert';
  if (a.includes('SUBMIT')) return 'warn';
  return 'run';
}

export default function DetailTimelineColumn({
  task,
  progress,
  completeness,
  approval,
  timeline,
  logs,
}: DetailTimelineColumnProps) {
  const nodes: TlNode[] = [];

  for (const log of logs ?? []) nodes.push(auditNode(log));
  for (const it of timeline ?? []) {
    nodes.push({
      key: `tl-${it.at}-${it.action}`,
      title: it.nodeName || it.action,
      time: it.at,
      actor: it.actorName ?? undefined,
      desc: it.comment ?? undefined,
      tone: approvalTone(it.action),
    });
  }
  nodes.sort((a, b) => {
    const ta = a.time ? Date.parse(a.time) : 0;
    const tb = b.time ? Date.parse(b.time) : 0;
    return ta - tb;
  });

  if (approval) {
    const active = (approval.nodes ?? []).find((n) => n.status === 'IN_PROGRESS');
    if (active) {
      const waitingDays = Math.floor((Date.now() - Date.parse(approval.startedAt)) / 86_400_000);
      nodes.push({
        key: `node-${active.id}`,
        title: active.name,
        actor: active.approverRoleCodes.join(' / ') || undefined,
        desc: `等待中 · SLA ${active.slaHours}h · 已等待 ${Math.max(waitingDays, 0)} 天`,
        tone: 'warn',
        pending: true,
      });
    }
    for (const n of approval.nodes ?? []) {
      if (n.status !== 'PENDING') continue;
      nodes.push({
        key: `node-${n.id}`,
        title: n.name,
        actor: n.approverRoleCodes.join(' / ') || undefined,
        desc: `${n.mode === 'ALL' ? '会签' : '或签'} · SLA ${n.slaHours}h · 待触发`,
        tone: 'idle',
        pending: true,
      });
    }
  }

  /* P7.8：逾期/剩余天数按自然日计算（忽略时分秒），口径与后端一致。 */
  const todayUtc = (() => {
    const n = new Date();
    return Date.UTC(n.getFullYear(), n.getMonth(), n.getDate());
  })();
  const overdueDays = (() => {
    if (!task.planEnd || ['FROZEN', 'ARCHIVED', 'CANCELLED'].includes(task.state)) return 0;
    const end = Date.parse(task.planEnd);
    if (Number.isNaN(end)) return 0;
    const diff = todayUtc - end;
    return diff > 0 ? Math.floor(diff / 86_400_000) : 0;
  })();
  const remainDays =
    task.planEnd && !Number.isNaN(Date.parse(task.planEnd))
      ? Math.ceil((Date.parse(task.planEnd) - todayUtc) / 86_400_000)
      : null;
  const pct = progress?.progressPct ?? task.progressPct ?? 0;
  /* P6.51：无「需附件要素」时齐套率为 null，显示「—」而不是 0% */
  const completenessText =
    completeness?.completenessPct === null || completeness?.completenessPct === undefined
      ? '—'
      : `${completeness.completenessPct.toFixed(0)}%`;

  return (
    <section className="dt-card dcol" data-col="timeline" aria-label="审批与留痕">
      <header className="dt-card__head">
        <h3 className="dt-card__title">审批流程</h3>
        <span className="dt-card__extra">{nodes.length} 个节点</span>
      </header>

      <div className="dt-card__body">
        <div className="dt-tl">
          {nodes.length === 0 ? (
            <div className="dt-tl__empty">暂无审批与留痕记录</div>
          ) : (
            nodes.map((n) => (
              <div className={['dt-tl__item', n.pending ? 'pending' : ''].join(' ')} key={n.key}>
                <span className={['dt-tl__dot', `dt-tl__dot--${n.tone}`].join(' ')} />
                <div className="dt-tl__main">
                  <div className="dt-tl__title">{n.title}</div>
                  <div className="dt-tl__meta">
                    {[fmt(n.time), n.actor].filter(Boolean).join(' · ') || '待处理'}
                  </div>
                  {n.desc && <div className="dt-tl__desc">{n.desc}</div>}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="dt-kvs">
          <div className="dt-kvs__title">变更留痕 · 不可篡改</div>
          <div className="dt-kv__row">
            <span>任务进度</span>
            <b className="num">{pct.toFixed(1)}%</b>
          </div>
          <div className="dt-kv__row">
            <span>指标达标</span>
            <b className="num">
              {progress?.metricAchieved ?? 0}/{progress?.metricTotal ?? 0}
            </b>
          </div>
          <div className="dt-kv__row">
            <span>产出物齐套</span>
            <b className="num">{completenessText}</b>
          </div>
          <div className="dt-kv__row">
            <span>逾期风险</span>
            <b className={overdueDays > 0 ? 'dt-kv__alert' : ''}>
              {overdueDays > 0
                ? `已逾期 ${overdueDays} 天`
                : remainDays !== null
                  ? `计划剩余 ${remainDays} 天`
                  : '未设计划'}
            </b>
          </div>
          <div className="dt-kv__row">
            <span>冻结状态</span>
            <b>{task.frozenAt ? `v${task.freezeVersion} · ${fmt(task.frozenAt)}` : '未冻结'}</b>
          </div>
          <div className="dt-kv__row">
            <span>操作记录</span>
            <b>
              <Badge tone="idle">{logs?.length ?? 0} 条</Badge>
            </b>
          </div>
        </div>
      </div>
    </section>
  );
}
